// Utils de dates pour réunions (Europe/Paris) sans dépendances externes

const FRENCH_DAYS = [
  "Dimanche",
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
];
const FRENCH_MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

const PARIS_TIME_ZONE = "Europe/Paris";
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAY_LABELS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const PARIS_PARTS_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: PARIS_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const PARIS_WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: PARIS_TIME_ZONE,
  weekday: "short",
});

function getParisParts(date) {
  const parts = PARIS_PARTS_FORMATTER.formatToParts(date);
  const data = {};
  parts.forEach(({ type, value }) => {
    if (type !== "literal") {
      data[type] = Number(value);
    }
  });
  const weekdayLabel = PARIS_WEEKDAY_FORMATTER.format(date).toLowerCase();
  return {
    year: data.year,
    month: data.month,
    day: data.day,
    hour: data.hour,
    minute: data.minute,
    second: data.second,
    weekday: WEEKDAY_LABELS.indexOf(weekdayLabel.slice(0, 3)),
  };
}

function getParisOffset(date) {
  const parts = PARIS_PARTS_FORMATTER.formatToParts(date);
  const data = {};
  parts.forEach(({ type, value }) => {
    if (type !== "literal") {
      data[type] = Number(value);
    }
  });
  const asUTC = Date.UTC(
    data.year,
    data.month - 1,
    data.day,
    data.hour,
    data.minute,
    data.second,
  );
  return asUTC - date.getTime();
}

export function convertParisLocalToUtc(parisLocalDate) {
  const offset = getParisOffset(parisLocalDate);
  return parisLocalDate.getTime() - offset;
}

const OFFICIAL_SLOTS = [
  { weekday: 3, hour: 19, minute: 30 }, // Mercredi
  { weekday: 6, hour: 18, minute: 0 },  // Samedi
];

function findOfficialSlot(weekday) {
  return weekday === 3
    ? OFFICIAL_SLOTS[0]
    : OFFICIAL_SLOTS[1];
}

function isOfficialSlot(parts) {
  return (
    (parts.weekday === 3 && parts.hour === 19 && parts.minute === 30) ||
    (parts.weekday === 6 && parts.hour === 18 && parts.minute === 0)
  );
}

export function formatMeetingLabel(date) {
  const parts = getParisParts(date);
  const dayName = FRENCH_DAYS[parts.weekday];
  const monthName = FRENCH_MONTHS[parts.month - 1];
  const hours = String(parts.hour).padStart(2, "0");
  const minutes = String(parts.minute).padStart(2, "0");
  return `${dayName} ${parts.day} ${monthName} — ${hours}:${minutes}`;
}

export function buildMeetingDate(targetDow, hour, minute, from = new Date()) {
  // targetDow: 0=Dimanche ... 6=Samedi (comme Date.getDay)
  const fromParts = getParisParts(from);
  let deltaDays = (targetDow - fromParts.weekday + 7) % 7;
  const currentMinutes = fromParts.hour * 60 + fromParts.minute;
  const targetMinutes = hour * 60 + minute;
  if (deltaDays === 0 && currentMinutes >= targetMinutes) {
    deltaDays = 7;
  }

  const localCandidate = new Date(
    Date.UTC(
      fromParts.year,
      fromParts.month - 1,
      fromParts.day + deltaDays,
      hour,
      minute,
      0,
      0,
    ),
  );
  const timestamp = convertParisLocalToUtc(localCandidate);
  return new Date(timestamp);
}

export function addParisDays(date, days) {
  const parts = getParisParts(date);
  const localCandidate = new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day + days,
      parts.hour,
      parts.minute,
      parts.second,
      0,
    ),
  );
  const timestamp = convertParisLocalToUtc(localCandidate);
  return new Date(timestamp);
}

// Fonction pour créer des dates spéciales avec description
function createSpecialDate(year, month, day, hour, minute, description, specialType = "event") {
  const localDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
  const meetingAt = convertParisLocalToUtc(localDate);
  const labelDate = new Date(meetingAt);
  const isoDate = toIsoDate(meetingAt);
  const label = description
    ? `${formatMeetingLabel(labelDate)} : ${description}`
    : formatMeetingLabel(labelDate);
  return {
    label,
    labelSimple: formatMeetingLabel(labelDate),
    meetingAt,
    isoDate,
    isSpecial: true,
    specialType,
  };
}

// Import dynamique pour éviter les dépendances circulaires
let getSpecialEventsFromFirebase = null;
let getExcludedDatesFromFirebase = null;
let listenToSpecialEventsFromFirebase = null;
let listenToExcludedDatesFromFirebase = null;

export function setSpecialEventsHelpers(getEvents, getDates, listenToEvents = null, listenToDates = null) {
  getSpecialEventsFromFirebase = getEvents;
  getExcludedDatesFromFirebase = getDates;
  listenToSpecialEventsFromFirebase = listenToEvents;
  listenToExcludedDatesFromFirebase = listenToDates;
  
  // Si les listeners sont disponibles, démarrer l'écoute en temps réel
  if (listenToEvents && listenToDates) {
    startReactiveCache();
  }
}

// Version synchrone avec cache pour compatibilité
let cachedMeetings = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache réactif pour les événements et dates exclues
let cachedSpecialEvents = [];
let cachedExcludedDates = [];
let cacheUpdateCallbacks = new Set();

// Fonction pour démarrer l'écoute réactive
function startReactiveCache() {
  if (!listenToSpecialEventsFromFirebase || !listenToExcludedDatesFromFirebase) {
    return;
  }

  console.log('🔄 [dateHelpers] Démarrage du cache réactif');

  // Écouter les événements spéciaux
  listenToSpecialEventsFromFirebase((events) => {
    console.log('🔄 [dateHelpers] Événements mis à jour:', events.length);
    cachedSpecialEvents = events || [];
    invalidateCache();
  });

  // Écouter les dates exclues
  listenToExcludedDatesFromFirebase((dates) => {
    console.log('🔄 [dateHelpers] Dates exclues mises à jour:', dates.length);
    cachedExcludedDates = dates || [];
    invalidateCache();
  });
}

// Fonction pour invalider le cache et notifier les callbacks
function invalidateCache() {
  cachedMeetings = null;
  cacheTimestamp = 0;
  
  // Notifier tous les callbacks enregistrés
  cacheUpdateCallbacks.forEach(callback => {
    try {
      callback();
    } catch (error) {
      console.warn('⚠️ [dateHelpers] Erreur callback cache:', error);
    }
  });
}

// Fonction pour enregistrer un callback de mise à jour
export function onMeetingsCacheUpdate(callback) {
  cacheUpdateCallbacks.add(callback);
  return () => {
    cacheUpdateCallbacks.delete(callback);
  };
}

export function getNextMeetings(limit = 8) {
  // Si le cache est valide et que les helpers sont initialisés, retourner le cache
  if (cachedMeetings && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
    return cachedMeetings.slice(0, limit);
  }
  
  // Sinon, retourner les valeurs par défaut (synchrones)
  const results = [];
  const now = new Date();
  
  // Dates à exclure par défaut
  const excludedDates = new Set([
    "2025-03-04", // Mercredi 4 mars 2025
    "2025-03-07", // Samedi 7 mars 2025
  ]);
  
  const occurrences = [];
  let nextWed = buildMeetingDate(3, 19, 30, now);
  let nextSat = buildMeetingDate(6, 18, 0, now);
  
  for (let i = 0; i < limit * 2; i++) {
    occurrences.push(new Date(nextWed));
    occurrences.push(new Date(nextSat));
    nextWed = addParisDays(nextWed, 7);
    nextSat = addParisDays(nextSat, 7);
  }
  
  occurrences.sort((a, b) => a.getTime() - b.getTime());
  
  for (const d of occurrences) {
    if (results.length >= limit) break;
    
    const meetingAt = d.getTime();
    const isoDate = toIsoDate(meetingAt);
    
    if (excludedDates.has(isoDate)) {
      continue;
    }
    
    results.push({
      label: formatMeetingLabel(d),
      meetingAt,
      isoDate,
    });
  }
  
  return results;
}

// Version asynchrone pour charger depuis Firebase
export async function getNextMeetingsAsync(limit = 8) {
  const results = [];
  const now = new Date();

  // Utiliser le cache réactif si disponible, sinon récupérer depuis Firebase
  let specialDates = [];
  let excludedDates = new Set();
  
  try {
    // Si le cache réactif est disponible, l'utiliser
    if (cachedSpecialEvents.length > 0 || cachedExcludedDates.length > 0) {
      specialDates = cachedSpecialEvents.map(event => {
        // Si l'événement a déjà un meetingAt (timestamp), l'utiliser directement
        // Sinon, créer la date depuis year/month/day/hour/minute
        let meetingAt;
        if (event.meetingAt) {
          meetingAt = event.meetingAt;
        } else {
          // Créer une date locale (comme si c'était en heure de Paris)
          const localDate = new Date(Date.UTC(event.year, event.month - 1, event.day, event.hour, event.minute, 0, 0));
          // Convertir cette date locale de Paris en UTC
          meetingAt = convertParisLocalToUtc(localDate);
        }
        const labelDate = new Date(meetingAt);
        const isoDate = toIsoDate(meetingAt);
        const label = `${formatMeetingLabel(labelDate)} : ${event.name}`;
        const eventColor = event.color || 'orange';
        
        // Log pour déboguer
        console.log('🔄 [dateHelpers] Événement spécial (cache réactif):', {
          name: event.name,
          isoDate,
          color: eventColor,
          colorFromFirebase: event.color,
          hasColor: !!event.color
        });
        
        return {
          label,
          labelSimple: formatMeetingLabel(labelDate),
          meetingAt,
          isoDate,
          isSpecial: true,
          specialType: 'event',
          color: eventColor,
        };
      });
      excludedDates = new Set(cachedExcludedDates.map(d => d.isoDate));
    } else if (getSpecialEventsFromFirebase && getExcludedDatesFromFirebase) {
      // Sinon, récupérer depuis Firebase (première fois ou si le cache réactif n'est pas encore initialisé)
      const events = await getSpecialEventsFromFirebase();
      specialDates = events.map(event => {
        let meetingAt;
        if (event.meetingAt) {
          meetingAt = event.meetingAt;
        } else {
          const localDate = new Date(Date.UTC(event.year, event.month - 1, event.day, event.hour, event.minute, 0, 0));
          meetingAt = convertParisLocalToUtc(localDate);
        }
        const labelDate = new Date(meetingAt);
        const isoDate = toIsoDate(meetingAt);
        const label = `${formatMeetingLabel(labelDate)} : ${event.name}`;
        const eventColor = event.color || 'orange';
        
        // Log pour déboguer
        console.log('🔄 [dateHelpers] Événement spécial (fallback):', {
          name: event.name,
          isoDate,
          color: eventColor,
          colorFromFirebase: event.color,
          hasColor: !!event.color
        });
        
        return {
          label,
          labelSimple: formatMeetingLabel(labelDate),
          meetingAt,
          isoDate,
          isSpecial: true,
          specialType: 'event',
          color: eventColor,
        };
      });
      
      const dates = await getExcludedDatesFromFirebase();
      excludedDates = new Set(dates.map(d => d.isoDate));
    }
  } catch (error) {
    console.warn('⚠️ [dateHelpers] Erreur récupération Firebase, utilisation des valeurs par défaut:', error);
    // Valeurs par défaut si Firebase échoue
    excludedDates = new Set([
      "2025-03-04", // Mercredi 4 mars 2025
      "2025-03-07", // Samedi 7 mars 2025
    ]);
  }

  // Créer un Set des timestamps des événements spéciaux pour éviter les doublons
  const specialDatesSet = new Set(specialDates.map(sd => sd.meetingAt));
  const specialDatesIsoSet = new Set(specialDates.map(sd => sd.isoDate));

  const occurrences = [];
  let nextWed = buildMeetingDate(3, 19, 30, now);
  let nextSat = buildMeetingDate(6, 18, 0, now);

  const nowTs = Date.now();

  // Générer les occurrences régulières en excluant celles qui correspondent à des événements spéciaux
  for (let i = 0; i < limit * 2; i++) {
    const wedTs = nextWed.getTime();
    const wedIso = toIsoDate(wedTs);
    
    // Ne pas ajouter si c'est un événement spécial ou une date exclue
    if (!specialDatesSet.has(wedTs) && !specialDatesIsoSet.has(wedIso) && !excludedDates.has(wedIso) && wedTs >= nowTs) {
      occurrences.push(new Date(nextWed));
    }
    
    const satTs = nextSat.getTime();
    const satIso = toIsoDate(satTs);
    
    // Ne pas ajouter si c'est un événement spécial ou une date exclue
    if (!specialDatesSet.has(satTs) && !specialDatesIsoSet.has(satIso) && !excludedDates.has(satIso) && satTs >= nowTs) {
      occurrences.push(new Date(nextSat));
    }
    
    nextWed = addParisDays(nextWed, 7);
    nextSat = addParisDays(nextSat, 7);
  }

  // Ajouter les événements spéciaux
  specialDates.forEach((specialDate) => {
    if (specialDate.meetingAt >= nowTs) {
      occurrences.push(new Date(specialDate.meetingAt));
    }
  });

  occurrences.sort((a, b) => a.getTime() - b.getTime());

  // Utiliser un Set pour éviter les doublons basés sur le timestamp
  const seenTimestamps = new Set();

  for (const d of occurrences) {
    if (results.length >= limit) break;

    const meetingAt = d.getTime();
    
    // Éviter les doublons
    if (seenTimestamps.has(meetingAt)) {
      continue;
    }
    seenTimestamps.add(meetingAt);

    const isoDate = toIsoDate(meetingAt);

    // Vérifier les dates spéciales existantes
    const specialDate = specialDates.find((sd) => sd.meetingAt === meetingAt);
    if (specialDate) {
      results.push(specialDate);
      continue;
    }

    if (excludedDates.has(isoDate)) {
      continue;
    }

    results.push({
      label: formatMeetingLabel(d),
      meetingAt,
      isoDate,
    });
  }

  // Mettre à jour le cache pour que getNextMeetings puisse l'utiliser
  cachedMeetings = results;
  cacheTimestamp = Date.now();

  return results;
}

// Nouveaux helpers pour la vue d’attente
export function toIsoDate(ts) {
  const parts = getParisParts(new Date(ts));
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

export function sameDay(tsA, tsB) {
  const a = getParisParts(new Date(tsA));
  const b = getParisParts(new Date(tsB));
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

export function formatMeetingLabelTs(ts) {
  return formatMeetingLabel(new Date(ts));
}

export function formatMeetingDateLong(ts) {
  if (typeof ts !== "number" || Number.isNaN(ts)) return "";
  const parts = getParisParts(new Date(ts));
  const dayName = FRENCH_DAYS[parts.weekday];
  const monthName = FRENCH_MONTHS[parts.month - 1];
  return `${dayName} ${parts.day} ${monthName}`;
}

export function formatMeetingTime(ts) {
  if (typeof ts !== "number" || Number.isNaN(ts)) return "";
  const parts = getParisParts(new Date(ts));
  return `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

export function formatMeetingDateTime(ts) {
  if (typeof ts !== "number" || Number.isNaN(ts)) return "";
  return `${formatMeetingDateLong(ts)} — ${formatMeetingTime(ts)}`;
}

export function normalizeMeetingTimestamp(request) {
  const ts = typeof request?.meetingAt === "number" ? request.meetingAt : null;
  const iso = request?.isoDate || (ts ? toIsoDate(ts) : null);

  if (ts) {
    // Si la date du timestamp correspond déjà à isoDate, on conserve l'heure exacte.
    // Cela permet de garder les horaires des réunions spéciales (ex: 10:00).
    if (request?.isoDate && toIsoDate(ts) === request.isoDate) {
      return ts;
    }

    const parts = getParisParts(new Date(ts));
    if (isOfficialSlot(parts)) {
      return ts;
    }
  }

  if (!iso) {
    return ts;
  }

  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) {
    return ts;
  }

  // Créer une date locale Paris à midi pour obtenir le bon weekday
  const approxLocal = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
  const approxParts = getParisParts(approxLocal);
  const slot = findOfficialSlot(approxParts.weekday);
  const localDate = new Date(
    Date.UTC(
      approxParts.year,
      approxParts.month - 1,
      approxParts.day,
      slot.hour,
      slot.minute,
      0,
      0,
    ),
  );
  return convertParisLocalToUtc(localDate);
}
